import DOMPurify from 'dompurify';

const SAFE_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'a', 'img', 'hr', 'span', 'div', 'sub', 'sup'];
const SAFE_STYLES = new Set(['color', 'background-color', 'font-size', 'font-weight', 'font-style', 'text-align', 'text-decoration', 'border', 'border-collapse', 'padding', 'margin', 'max-width', 'width', 'height']);

export const safeContentUrl = (value, { image = false } = {}) => {
  const url = String(value || '').trim();
  if (!url || [...url].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return '';
  if (image && /^data:image\/(?:png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i.test(url)) return url;
  if (/^(?:https?:\/\/|\/(?!\/)|#)/i.test(url)) return url;
  if (!image && /^(?:mailto:|tel:)/i.test(url)) return url;
  // Existing articles may refer to an uploaded image by its Storage path.
  if (image && /^(?:gs:\/\/|blog\/images\/)/i.test(url)) return url;
  return '';
};

export const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

// The same allowlist is used for editor input, persisted content, and rendering
// old records. Never trust stored HTML merely because it came from Firestore.
export const sanitizeHtml = (value) => {
  const fragment = DOMPurify.sanitize(String(value || ''), {
    ALLOWED_TAGS: SAFE_TAGS,
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'width', 'height', 'colspan', 'rowspan', 'target', 'rel', 'style'],
    ALLOW_DATA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll('[href], [src], [style], [target]').forEach((element) => {
    for (const attr of ['href', 'src']) {
      if (!element.hasAttribute(attr)) continue;
      const url = safeContentUrl(element.getAttribute(attr), { image: attr === 'src' });
      if (url) element.setAttribute(attr, url);
      else element.removeAttribute(attr);
    }
    if (element.hasAttribute('style')) {
      const styles = [];
      for (const property of Array.from(element.style)) {
        const cssValue = element.style.getPropertyValue(property);
        if (SAFE_STYLES.has(property) && !/(?:url\s*\(|expression|var\s*\(|!important)/i.test(cssValue)) styles.push(`${property}: ${cssValue}`);
      }
      if (styles.length) element.setAttribute('style', styles.join('; '));
      else element.removeAttribute('style');
    }
    if (element.tagName === 'A') {
      if (element.getAttribute('target') === '_blank') element.setAttribute('rel', 'noopener noreferrer');
      else element.removeAttribute('target');
    }
  });
  const wrapper = document.createElement('div');
  wrapper.appendChild(fragment);
  return wrapper.innerHTML;
};

export const contentText = (value) => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = sanitizeHtml(value);
  return wrapper.textContent || '';
};
